import { frontendURL } from 'dashboard/helper/URLHelper';

export const CRM_CONTEXT_STORAGE_KEY = 'CRM_CONTEXT';
export const DEFAULT_REDIRECT_DELAY_MS = 2000;
export const CRM_CONTEXT_REDIRECT_DELAY_MS = 7000;
export const REDIRECTING_PAGE_NAME = 'REDIRECTING';
export const REDIRECTING_PHRASE_KEYS = Object.freeze([
  'REDIRECTING.PHRASES.STEP_ONE',
  'REDIRECTING.PHRASES.STEP_TWO',
  'REDIRECTING.PHRASES.STEP_THREE',
  'REDIRECTING.PHRASES.STEP_FOUR',
  'REDIRECTING.PHRASES.STEP_FIVE',
]);

const TRACKED_QUERY_KEYS = Object.freeze([
  'house_id',
  'agent_phone',
  'agent_email',
  'partner_3p_supply',
  'supply_origin',
  'click_origin',
  'crm_name',
  'creci',
]);

const cleanId = value => String(value || '').replace(/[^\w-]/g, '');

export const queryValue = (query, ...keys) => {
  const values = keys.map(key => {
    const value = query?.[key];
    if (Array.isArray(value) ? value[0] : value) {
      return Array.isArray(value) ? value[0] : value;
    }
    return '';
  });
  return values.find(Boolean) || '';
};

const withQuery = (path, query = {}) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, value);
    }
  });
  const serialized = params.toString();
  return serialized ? `${path}?${serialized}` : path;
};

const safeStorage = storage => {
  if (storage) return storage;
  if (typeof window === 'undefined') return null;
  return window.localStorage || null;
};

const safeGetItem = (storage, key) => {
  try {
    return storage?.getItem(key) || '';
  } catch {
    return '';
  }
};

const safeSetItem = (storage, key, value) => {
  try {
    storage?.setItem(key, value);
  } catch {
    // Storage can be unavailable in private browsing or tests.
  }
};

export const redirectingCompanyName = ({ query = {} } = {}) =>
  queryValue(query, 'crm_name', 'crmName') || '';

export const resolveRedirectDelay = ({ query = {}, storage } = {}) => {
  const crmName = redirectingCompanyName({ query });
  if (!crmName) return DEFAULT_REDIRECT_DELAY_MS;

  const currentStorage = safeStorage(storage);
  const storedCrmContext = safeGetItem(currentStorage, CRM_CONTEXT_STORAGE_KEY);
  if (crmName === storedCrmContext) return DEFAULT_REDIRECT_DELAY_MS;

  safeSetItem(currentStorage, CRM_CONTEXT_STORAGE_KEY, crmName);
  return CRM_CONTEXT_REDIRECT_DELAY_MS;
};

export const buildRedirectTrackingProperties = (query = {}) => {
  const extraProperties = TRACKED_QUERY_KEYS.reduce((acc, key) => {
    acc[key] = queryValue(query, key) || null;
    return acc;
  }, {});

  return {
    pageName: REDIRECTING_PAGE_NAME,
    extraProperties,
    ...extraProperties,
  };
};

export const resolveRedirectDestination = ({ accountId, query = {} } = {}) => {
  const account = cleanId(accountId);
  if (!account) return frontendURL('no-accounts');

  const crmHouseId = cleanId(
    queryValue(query, 'house_id', 'houseId', 'houseid')
  );
  const agentPhone = queryValue(query, 'agent_phone', 'agentPhone').trim();
  if (crmHouseId && agentPhone) {
    return withQuery(frontendURL(`accounts/${account}/visitas/agendar`), {
      houseid: crmHouseId,
      phoneNumber: agentPhone,
      countryCode: '55',
    });
  }

  const conversationId = cleanId(
    queryValue(query, 'conversation_id', 'conversationId', 'chat_id', 'chatId')
  );
  if (conversationId) {
    return frontendURL(`accounts/${account}/conversations/${conversationId}`);
  }

  const visitId = cleanId(
    queryValue(query, 'visit_id', 'visitId', 'visita_id')
  );
  if (visitId) return frontendURL(`accounts/${account}/visitas/${visitId}`);

  const imovelId = cleanId(
    queryValue(query, 'imovel_id', 'imovelId', 'house_id', 'houseId')
  );
  if (imovelId) {
    return withQuery(frontendURL(`accounts/${account}/imoveis/${imovelId}`), {
      context: queryValue(query, 'context', 'business_context'),
      from_route: queryValue(query, 'from_route', 'fromRoute', 'source'),
    });
  }

  const offerId = cleanId(queryValue(query, 'offer_id', 'offerId'));
  if (offerId) return frontendURL(`accounts/${account}/propostas/${offerId}`);

  const leadId = cleanId(queryValue(query, 'lead_id', 'leadId', 'contact_id'));
  if (leadId) return frontendURL(`accounts/${account}/history/${leadId}`);

  const target = String(queryValue(query, 'target', 'to') || '').toLowerCase();
  const safeTargets = {
    agenda: frontendURL(`accounts/${account}/agenda`),
    visitas: frontendURL(`accounts/${account}/visitas`),
    imoveis: frontendURL(`accounts/${account}/imoveis`),
    crm: frontendURL(`accounts/${account}/crm`),
    leads: frontendURL(`accounts/${account}/leads`),
    propostas: frontendURL(`accounts/${account}/propostas`),
    ofertas: frontendURL(`accounts/${account}/propostas`),
  };

  return safeTargets[target] || frontendURL(`accounts/${account}/inicio`);
};
